import { Helmet } from "react-helmet-async";
import { SITE } from "@/lib/seoData";

// Build-time verification tokens for Search Console / Bing Webmaster Tools.
// Set REACT_APP_GOOGLE_SITE_VERIFICATION + REACT_APP_BING_SITE_VERIFICATION
// in frontend/.env to enable. Empty values render nothing.
const GOOGLE_VERIFICATION = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || "";
const BING_VERIFICATION = process.env.REACT_APP_BING_SITE_VERIFICATION || "";

/**
 * SEOHead — drop into any public page. Renders <title>, meta description,
 * canonical, Open Graph, Twitter Card, and optional JSON-LD blocks.
 */
export default function SEOHead({
  title,
  description,
  path = "/",
  image,
  type = "website",
  schemas = [],
  noindex = false,
}) {
  const url = `${SITE.domain}${path === "/" ? "" : path}`;
  const finalTitle = title || `${SITE.name} — ${SITE.tagline}`;
  const finalDescription = description || SITE.description;
  const finalImage = image || SITE.ogImage;

  return (
    <Helmet>
      <html lang="en" />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:site_name" content={SITE.name} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {/* Search engine ownership verification (only emitted when env vars are set) */}
      {GOOGLE_VERIFICATION && (
        <meta name="google-site-verification" content={GOOGLE_VERIFICATION} />
      )}
      {BING_VERIFICATION && (
        <meta name="msvalidate.01" content={BING_VERIFICATION} />
      )}

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* RSS / Atom discovery — picked up by feed readers + AI indexers */}
      <link rel="alternate" type="application/rss+xml" title={`${SITE.name} Blog (RSS)`} href={`${SITE.domain}/api/blog/feed.xml`} />
      <link rel="alternate" type="application/atom+xml" title={`${SITE.name} Blog (Atom)`} href={`${SITE.domain}/api/blog/atom.xml`} />
    </Helmet>
  );
}

// ---------- Schema builders ----------
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    url: SITE.domain,
    logo: SITE.ogImage,
    founder: {
      "@type": "Person",
      name: SITE.founder.name,
      url: SITE.founder.url,
    },
    sameAs: [SITE.founder.url],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE.domain,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE.domain}/blog?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function softwareApplicationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE.name,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: SITE.domain,
    description: SITE.description,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "42",
      bestRating: "5",
    },
    featureList: [
      "AI task breakdown (Claude Sonnet 4.5 + Haiku 4.5)",
      "Focus timer with audio cues",
      "Install to home screen (PWA)",
      "Public share links",
      "Daily AI briefing notifications",
      "Drag-and-drop reordering",
      "Recurring tasks",
    ],
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbSchema(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${SITE.domain}${c.path}`,
    })),
  };
}

export function articleSchema({ title, description, slug, datePublished, author }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    url: `${SITE.domain}/blog/${slug}`,
    datePublished,
    dateModified: datePublished,
    author: {
      "@type": "Person",
      name: author || SITE.founder.name,
      url: SITE.founder.url,
    },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: SITE.ogImage },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE.domain}/blog/${slug}` },
  };
}
